import { $, pageLoaded } from "venuescanner-js/tools/elemental";
import { scrollToElement } from "venuescanner-js/vanilla/components/animateScroll";
import guid from "venuescanner-js/utils/guid";
import loadExternalLibrary from "venuescanner-js/utils/loadExternalLibrary";

import assetPath from "venuescanner-js/constants/asset_path";
import "./map.scss";
let loadTimeout = null;
let dependenciesFetched = false;
const sources = {
  MAPBOX: "mapbox",
  TRAVELTIME: "traveltime",
};
const mapSource = sources.TRAVELTIME;
const mapStyle = assetPath(
  "https://tiles.venuescanner.com/maps/basic/256/{z}/{x}/{y}.png?key=wKXLCdzWGsdzzaaT4TQ3"
);

class Map {
  loading = false;
  loaded = false;
  container = null;
  containerId = null;
  map = null;
  padding = 20;
  markerIcon = null;
  regionIcon = null;
  onLoaded = [];
  constructor(
    { container = "#map", padding = 20 } = { container: "#map", padding: 20 }
  ) {
    this.padding = padding;
    this.containerSelector = container;
    this.container =
      container === HTMLElement ? container : $(container).get(0);

    if (typeof container !== "string") {
      this.loadMap();
      pageLoaded(() => {
        this.dependencies();
      });

      return;
    }

    this.mapElement = document.querySelector(container);

    if (document.readyState === "complete") {
      return this.createObserver();
    }

    window.addEventListener(
      "load",
      (_event) => {
        // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
        // We're adding an intersection observer to only load the map when we're closed
        // to the component

        this.createObserver();
      },
      false
    );
  }

  ready() {
    // console.log('%cMap loaded', 'color: blue;font-size:11px')
    if (!this.map) {
      if (mapSource === sources.MAPBOX) {
        window.mapboxgl.accessToken =
          "pk.eyJ1IjoidmVudWVzY2FubmVyIiwiYSI6ImNqbHF3N240cDJrbXYza3MxZ2o0ZDc2dW0ifQ.7sM_TaZcQWaEejh9IoDU2Q";
        this.map = new window.mapboxgl.Map({
          container: this.containerId,
          style:
            "mapbox://styles/venuescanner/cjmbscp89i44p2ss4ga0qgslh?optimize=true",
          center: this.center || [51.5182237, -0.123061],
          zoom: 14,
        });
        window.mapboxgl.baseApiUrl = "https://mapbox.venuescanner.com";
        this.map.setMaxZoom(15);
        this.map.scrollZoom.disable();
        this.map.addControl(
          new window.mapboxgl.NavigationControl({ showCompass: false }),
          "top-left"
        );
      } else if (mapSource === sources.TRAVELTIME) {
        this.map = window.L.map(this.container, {
          preferCanvas: true,
          dragging: !window.L.Browser.mobile,
        }).setView(this.center || [51.5182237, -0.123061], 14);
        window.L.tileLayer(mapStyle, {
          attribution:
            '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
        }).addTo(this.map);
        this.markerIcon = window.L.divIcon({
          className: "map-pin-static",
          iconSize: [15, 19],
          iconAnchor: [7, 18],
        });
        this.regionIcon = window.L.divIcon({
          className: "map-pin-region",
          iconSize: [15, 19],
          iconAnchor: [7, 18],
        });
      }
    }
    // this.map = window.L.mapbox.map(this.containerId, 'mapbox.streets').setView([51.5182237, -0.123061], 14)
    // window.L.mapbox.styleLayer('mapbox://styles/venuescanner/cjmbscp89i44p2ss4ga0qgslh').addTo(this.map)

    this.loaded = true;
    while (this.onLoaded.length) {
      this.onLoaded.shift()();
    }
  }

  loadMap() {
    if (this.container) {
      if (!this.container.id) {
        this.container.id = guid();
      }
      this.containerId = this.container.id;
      this.dependencies();
    }
  }

  detected() {
    if (mapSource === sources.MAPBOX) {
      return typeof window.mapboxgl !== "undefined";
    } else if (mapSource === sources.TRAVELTIME) {
      return typeof window.L !== "undefined";
    }
  }

  dependencies() {
    if (dependenciesFetched) this.loading = true;
    if (!this.loading && !this.detected()) {
      // start loading dependencies

      console.log("%cMap loading", "color: grey;font-size:11px");
      if (mapSource === sources.MAPBOX) {
        loadExternalLibrary({
          src: "https://api.tiles.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js",
          type: "text/javascript",
          async: true,
        });
        loadExternalLibrary({
          href: "https://api.tiles.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.css",
          async: true,
        });
      } else if (mapSource === sources.TRAVELTIME) {
        loadExternalLibrary({
          // src: '/lib/leaflet.js',
          src: "https://unpkg.com/leaflet@1.7.1/dist/leaflet.js",
          type: "text/javascript",
          async: true,
        });
        loadExternalLibrary({
          href: "https://unpkg.com/leaflet@1.7.1/dist/leaflet.css",
          async: true,
        });
      }
      dependenciesFetched = true;
      this.loading = true;
      loadTimeout = setTimeout(() => {
        this.dependencies();
      }, 50);
    } else if (this.loading && !this.detected()) {
      // not finished loading dependencies
      loadTimeout = setTimeout(() => {
        this.dependencies();
      }, 50);
    } else {
      // loaded dependencies
      clearTimeout(loadTimeout);
      loadTimeout = null;
      this.ready();
    }
  }

  markers = [];
  clearMarkers() {
    while (this.markers.length) {
      this.markers.shift().remove();
    }
  }

  removeMarker(...markers) {
    while (markers.length) {
      const marker = markers.shift();
      if (marker) {
        this.markers = this.markers.filter((m) => m !== marker);
        marker.remove();
      }
    }
  }

  drawMarker({
    lat,
    lng,
    center = false,
    onClick,
    onContext,
    onMove,
    onDrop,
    onHover,
  }) {
    let marker;
    if (mapSource === sources.MAPBOX) {
      const newMarker = $("<div>").addClass("marker").get(0);
      marker = new window.mapboxgl.Marker(newMarker)
        .setLngLat([lng, lat])
        .addTo(this.map);
    } else if (mapSource === sources.TRAVELTIME) {
      const options = {
        icon: this.markerIcon,
      };
      if (onMove || onDrop) options.draggable = true;
      marker = window.L.marker([lat, lng], options).addTo(this.map);
      if (onMove) {
        marker.on("drag", (event) => {
          const position = marker.getLatLng();
          onMove(position);
        });
      }
      // console.log('options', options)
      if (onDrop) {
        // console.log('dragEnd')
        marker.on("dragend", (event) => {
          const position = marker.getLatLng();
          onDrop(position);
        });
      }
    }

    const markerEl = marker.getElement();
    if (center) {
      setTimeout(() => {
        this.fitMarkers([marker]);
      }, 100);
    }
    if (onClick) {
      $(markerEl).bind("click", onClick);
    }
    if (onContext) {
      $(markerEl).bind("contextmenu", (e) => {
        e.preventDefault();
        onContext(e, marker);
        return false;
      });
    }
    if (onHover) {
      $(markerEl).bind("mouseenter", (...args) => {
        onHover(true, markerEl, ...args);
      });
      $(markerEl).bind("mouseleave", (...args) => {
        onHover(false, markerEl, ...args);
      });
    }
    this.markers.push(marker);
    return { element: markerEl, marker };
  }

  drawRegion({ lat, lng, center = false, showMarker = true, radius }) {
    let marker;

    let circle;
    if (mapSource === sources.TRAVELTIME) {
      const options = {
        icon: this.regionIcon,
      };
      if (showMarker)
        marker = window.L.marker([lat, lng], options).addTo(this.map);
      if (radius) {
        circle = window.L.circle([lat, lng], { radius }).addTo(this.map);
      }
    }
    // console.log(circle)

    const markerEl = marker ? marker.getElement() : null;
    if (center && marker) {
      setTimeout(() => {
        this.fitMarkers([marker]);
      }, 100);
    }
    if (marker) this.markers.push(marker);
    return { element: markerEl, marker, circle };
  }

  marker(...args) {
    return new Promise((resolve) => {
      if (this.loaded) {
        resolve(this.drawMarker(...args));
      } else {
        this.onLoaded.push(() => {
          resolve(this.drawMarker(...args));
        });
      }
    });
  }

  disableZoom() {
    return new Promise((resolve) => {
      if (this.loaded) {
        this.map.touchZoom.disable();
        this.map.doubleClickZoom.disable();
        this.map.scrollWheelZoom.disable();
        resolve();
      } else {
        this.onLoaded.push(() => {
          this.map.touchZoom.disable();
          this.map.doubleClickZoom.disable();
          this.map.scrollWheelZoom.disable();
          resolve();
        });
      }
    });
  }

  region(...args) {
    return new Promise((resolve) => {
      if (this.loaded) {
        resolve(this.drawRegion(...args));
      } else {
        this.onLoaded.push(() => {
          resolve(this.drawRegion(...args));
        });
      }
    });
  }

  firstFit = true;
  fitMarkers(markers, initBounds) {
    if (mapSource === sources.TRAVELTIME) this.map.invalidateSize();
    markers = markers || this.markers;
    const points = markers
      .filter((r) => !!r)
      .map((m) => {
        return mapSource === sources.MAPBOX ? m.getLngLat() : m.getLatLng();
      })
      .filter((r) => !!r);
    const firstPin = initBounds ? null : points.shift();
    // console.log({ firstPin })
    const bounds =
      initBounds ||
      (mapSource === sources.MAPBOX
        ? new window.mapboxgl.LngLatBounds(firstPin, firstPin)
        : window.L.latLngBounds(firstPin, firstPin));
    points.forEach((coord) => bounds.extend(coord));
    const minLng = 0.01;
    const padding =
      mapSource === sources.MAPBOX
        ? this.padding
        : [this.padding, this.padding];
    if (bounds.getEast() - bounds.getWest() < minLng) {
      const center = bounds.getCenter();
      bounds.extend(
        mapSource === sources.MAPBOX
          ? [center.lng + minLng / 2, center.lat]
          : window.L.latLng(center.lat, center.lng + minLng / 2)
      );
      bounds.extend(
        mapSource === sources.MAPBOX
          ? [center.lng - minLng / 2, center.lat]
          : window.L.latLng(center.lat, center.lng - minLng / 2)
      );
    }
    if (bounds.getNorth() - bounds.getSouth() < minLng) {
      const center = bounds.getCenter();
      bounds.extend(
        mapSource === sources.MAPBOX
          ? [center.lng, center.lat + minLng / 2]
          : window.L.latLng(center.lat + minLng / 2, center.lng)
      );
      bounds.extend(
        mapSource === sources.MAPBOX
          ? [center.lng, center.lat - minLng / 2]
          : window.L.latLng(center.lat - minLng / 2, center.lng)
      );
    }
    // window.L.rectangle(bounds, { color: '#ff7800', weight: 1 }).addTo(this.map)
    // this.map.setView(firstPin, 13)
    this.map.fitBounds(bounds, {
      padding,
      duration: this.firstFit ? 200 : null,
    });
    this.firstFit = false;
  }

  show(circle) {
    this.fitMarkers(null, circle.getBounds());
  }

  getBounds() {
    return this.map.getBounds();
  }

  getCenter() {
    return this.map.getCenter();
  }

  resize() {
    if (mapSource === sources.TRAVELTIME) {
      this.map.invalidateSize();
      this.fitMarkers();
    } else {
      this.map.resize();
    }
  }

  boundsToRegion(bounds) {
    const boundary = bounds || this.map.getBounds();
    const [bounds1, bounds2] =
      mapSource === sources.TRAVELTIME
        ? [
            Object.values(boundary.getSouthWest()),
            Object.values(boundary.getNorthEast()),
          ]
        : [boundary.toArray()];
    const region =
      mapSource === sources.MAPBOX
        ? [...bounds1.reverse(), ...bounds2.reverse()]
        : [...bounds1, ...bounds2];
    return region;
  }

  mapBounds() {
    const bounds = this.map.getBounds();
    const { lat, lng } = bounds.getCenter();
    const centre = `${lat},${lng}`;
    const region = this.boundsToRegion(bounds);
    return { region, centre };
  }

  async applySpaceTiles() {
    const spaces = $(".space-tile").get();

    await Promise.all(
      spaces.map(async (space) => {
        const { lat, lng } = space.dataset;
        const { element, marker } = await this.marker({
          lat,
          lng,
          onClick: () => {
            scrollToElement(space);
          },
        });
        $(space)
          .bind("mouseenter", () => {
            $(element).addClass("hover");
          })
          .bind("mouseleave", () => {
            $(element).removeClass("hover");
          });
        return { element, marker };
      })
    );
    this.fitMarkers();
  }

  createObserver() {
    const options = {
      root: null,
      rootMargin: "70px",
    };

    const handleIntersect = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        this.loadMap();
        this.observer.unobserve(this.mapElement);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(this.mapElement);

    this.observer = observer;
  }
}

export { Map };
